import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { TERMS_RESUME_KEY, api, getToken, setToken } from '../lib/api.js';
import { CLIENT_SITE_URL } from '../lib/config.js';

/** Where the sign-in this tab started keeps its `state` until the ticket returns. */
export const ENTER_STATE_KEY = 'sa-workspace-enter-state';

/**
 * A relative in-app path, or `/`.
 *
 * THE SAME RULE AS frontend-client's `safeRelativePath()`: one leading slash,
 * never `//` or `/\` (browsers normalise a backslash into a slash, so `/\evil`
 * is protocol-relative), and nothing outside the same character class. The
 * server refusing `/\` while this page accepted it made the server's check the
 * only one — and this page is where the redirect actually happens.
 */
export function safeDestination(requested: string | null): string {
  if (requested === null || requested === '') return '/';
  if (!requested.startsWith('/')) return '/';
  if (requested.startsWith('//') || requested.startsWith('/\\')) return '/';
  return /^\/[A-Za-z0-9._~!$&'()*+,;=:@%/?-]*$/.test(requested) ? requested : '/';
}

/**
 * The page a Terms refusal kept for this tab (`TERMS_RESUME_KEY`), when it is a
 * same-origin path: one leading slash, never `//`, no backslash (`/\x` is
 * `//x` to a browser), no space or control character, bounded, and not this
 * page (which would resume into itself). A query and a hash are kept — unlike
 * `safeDestination()`, which also travels through the client site. Null for
 * anything else.
 */
export function safeResumePath(stored: string | null): string | null {
  if (stored === null || stored === '' || stored.length > 2048) return null;
  if (!stored.startsWith('/') || stored.startsWith('//')) return null;
  for (const char of stored) {
    const code = char.charCodeAt(0);
    if (char === '\\' || code <= 0x20 || code === 0x7f) return null;
  }
  if (/^\/enter(?:[/?#]|$)/.test(stored)) return null;
  return stored;
}

/** Read once and cleared, so a stale page is never resumed twice. */
function takeResumePath(): string | null {
  try {
    const stored = sessionStorage.getItem(TERMS_RESUME_KEY);
    sessionStorage.removeItem(TERMS_RESUME_KEY);
    return safeResumePath(stored);
  } catch {
    return null;
  }
}

/** Where a sign-in for `to` starts: here, so this tab mints the state first. */
export function enterHref(to: string): string {
  return `/enter?to=${encodeURIComponent(to)}`;
}

/** A fresh sign-in `state`: 32 random bytes, base64url — 43 characters that
 *  `HANDOFF_STATE_PATTERN` (shared) accepts. */
export function newState(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Where a sign-in from the client site starts and lands.
 *
 * The producer never sees this screen for more than an instant — it exchanges
 * the one-time ticket in the URL for the session they already have on
 * ScoutAround, then sends them where they were going. It exists so the
 * workspace can have no login of its own: the platform allows one active JWT
 * per user, so a second login form here revoked their session on the main site
 * and theirs revoked this one.
 *
 * THE TICKET IS BOUND TO THIS TAB. A ticket is somebody's whole session, and
 * one minted for an attacker's account, put in a link, used to sign a victim
 * into the attacker's workspace (login CSRF: the victim's screenplay then lands
 * in the attacker's production). So a sign-in STARTS here: with no ticket, this
 * page mints a random `state`, keeps it in sessionStorage and sends the browser
 * to the client site with it; the client site binds the ticket to it and sends
 * it back with the ticket. A ticket is redeemed only if its `state` is the one
 * this tab minted, and the client site checks the same pair again at redeem.
 */
export function EnterPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // StrictMode mounts effects twice in development, and the ticket is SINGLE
  // USE — the second exchange would always fail and show an expired-link error
  // on a perfectly good handoff.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    // BACK FROM THE TERMS SCREEN (spec 2026-10-29 §6): the website returns
    // here, never to the page itself, which stayed in this tab. A tab that
    // still holds its token goes straight there (or to the landing page); a
    // tab without one signs in as usual, for the same page.
    const resuming = params.get('resume') === '1';
    const resumePath = resuming ? takeResumePath() : null;
    if (resuming && getToken() !== null) {
      void navigate(resumePath ?? '/', { replace: true });
      return;
    }

    // Only a relative in-app path is honoured. An absolute or
    // protocol-relative `to` would make this an open redirect wearing our name.
    const destination = safeDestination(resumePath ?? params.get('to'));
    const ticket = params.get('t');

    if (!ticket) {
      // START: mint the state this tab will expect back, then go and ask.
      const state = newState();
      try {
        sessionStorage.setItem(ENTER_STATE_KEY, state);
      } catch {
        setError('This browser is not letting the workspace keep a sign-in in progress. Allow site data and try again.');
        return;
      }

      window.location.replace(
        `${CLIENT_SITE_URL}/production-workspace?state=${encodeURIComponent(state)}` +
          (destination === '/' ? '' : `&to=${encodeURIComponent(destination)}`),
      );
      return;
    }

    // LAND: only a ticket for the sign-in THIS tab started. Read once and
    // cleared, so a state cannot be replayed with a second ticket.
    let expected: string | null;
    try {
      expected = sessionStorage.getItem(ENTER_STATE_KEY);
      sessionStorage.removeItem(ENTER_STATE_KEY);
    } catch {
      expected = null;
    }

    const presented = params.get('state');
    if (expected === null || presented === null || presented !== expected) {
      // Not redeemed. Refusing is the point: an unrecognised ticket is either
      // stale or somebody else's session being handed to this browser.
      setError('That sign-in link was not started in this browser. Open the workspace from ScoutAround again.');
      return;
    }

    api
      .redeemHandoff(ticket, presented)
      .then((result) => {
        setToken(result.token);

        // A FULL LOAD, not a client-side navigate. `App` reads the token once
        // in a lazy state initializer, so a router navigation would land on the
        // "sign in on ScoutAround" screen holding a perfectly good session —
        // which is exactly what happened the first time this was wired. A
        // reload is also the honest thing at a sign-in boundary: every query
        // starts fresh against the new identity.
        //
        // `replace` so Back does not return to a spent ticket.
        window.location.replace(destination);
      })
      .catch(() => {
        setError('That sign-in link has expired. Open the workspace from ScoutAround again.');
      });
  }, [params, navigate]);

  return (
    <main
      data-testid="enter"
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div className="card1" style={{ width: 460, maxWidth: '100%', padding: 32 }}>
        <p className="kick">Production workspace</p>
        {error === null ? (
          <p className="dh" style={{ fontSize: 26, marginTop: 10 }} data-testid="enter-working">
            Signing you in…
          </p>
        ) : (
          <>
            <p className="dh" style={{ fontSize: 26, marginTop: 10 }}>
              That link has expired
            </p>
            <p
              className="mm"
              style={{ fontSize: 13, marginTop: 10, lineHeight: 1.6 }}
              data-testid="enter-error"
            >
              {error}
            </p>
            {/* Starts a fresh sign-in from THIS tab, which is the only kind
                this page will complete. */}
            <a
              className="btn btn-primary"
              style={{ marginTop: 18 }}
              href={enterHref(safeDestination(params.get('to')))}
              data-testid="enter-restart"
            >
              Sign in again
            </a>
          </>
        )}
      </div>
    </main>
  );
}
